import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, BookOpen, Award, Library, GitMerge, Trophy, Users, Building2,
  Brain, Settings, LogOut, Menu, X, GraduationCap, Calendar, Bell, RefreshCw, AlertTriangle
} from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, key: "dashboard" },
  { to: "/courses", label: "Cursos", icon: BookOpen, key: "courses" },
  { to: "/my-track", label: "Minha trilha", icon: GraduationCap, key: "my_track" },
  { to: "/certificates", label: "Certificados", icon: Award, key: "certificates" },
  { to: "/recycling", label: "Reciclagem", icon: RefreshCw, key: "recycling" },
  { to: "/alerts", label: "Alertas de compliance", icon: AlertTriangle, key: "alerts" },
  { to: "/library", label: "Biblioteca", icon: Library, key: "library" },
  { to: "/matrix", label: "Matriz de capacitação", icon: GitMerge, key: "matrix" },
  { to: "/ranking", label: "Ranking", icon: Trophy, key: "ranking" },
  { to: "/admin/users", label: "Colaboradores", icon: Users, key: "admin_users" },
  { to: "/admin/organization", label: "Estrutura", icon: Building2, key: "admin_organization" },
  { to: "/admin/courses", label: "Gerenciar cursos", icon: BookOpen, key: "admin_courses" },
  { to: "/admin/ai", label: "IA: gerar curso", icon: Brain, key: "admin_ai" },
  { to: "/admin/companies", label: "Empresas clientes", icon: Building2, key: "admin_companies" },
  { to: "/admin/settings", label: "Configurações", icon: Settings, key: "admin_settings" },
];

const ROLE_LABEL = {
  master_admin: "Master Admin",
  company_admin: "Admin da Empresa",
  manager: "Gestor",
  employee: "Colaborador",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    let timer;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/notifications/me/unread-count");
        setUnread(data.count || 0);
      } catch {}
    };
    fetchCount();
    timer = setInterval(fetchCount, 60000);
    return () => clearInterval(timer);
  }, [user]);

  if (!user) return null;

  const perms = user.screen_permissions || [];
  const items = NAV.filter((n) => perms.includes(n.key));

  return (
    <div className="min-h-screen flex bg-[var(--unigr-bg)]">
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className={
          "fixed lg:static z-40 inset-y-0 left-0 w-72 bg-white border-r border-[var(--unigr-border)] transform transition-transform lg:translate-x-0 " +
          (open ? "translate-x-0" : "-translate-x-full")
        }
      >
        <div className="h-20 flex items-center justify-between px-6 border-b border-[var(--unigr-border)]">
          <Logo size={34} />
          <button onClick={() => setOpen(false)} className="lg:hidden text-slate-500" data-testid="sidebar-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="px-3 py-4 space-y-0.5 overflow-y-auto h-[calc(100%-5rem)]">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace(/\//g, "-")}`}
              className={({ isActive }) =>
                "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors " +
                (isActive
                  ? "bg-[var(--unigr-navy)] text-white"
                  : "text-slate-700 hover:bg-slate-50")
              }
              onClick={() => setOpen(false)}
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="h-20 bg-white/80 backdrop-blur-xl border-b border-[var(--unigr-border)] flex items-center justify-between px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button onClick={() => setOpen(true)} className="lg:hidden text-slate-700" data-testid="sidebar-open">
              <Menu className="w-5 h-5" />
            </button>
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">UNIGR</div>
              <div className="font-display text-lg font-bold text-[var(--unigr-text)]">
                Olá, {user.name?.split(" ")[0] || "Colaborador"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/notifications" className="relative p-2 rounded-md hover:bg-slate-100" data-testid="notification-bell">
              <Bell className="w-5 h-5 text-slate-700" />
              {unread > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--unigr-red)] text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-badge">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </Link>
            <div className="hidden md:block text-right">
              <div className="text-xs text-slate-500">{ROLE_LABEL[user.role]}</div>
              <div className="text-sm font-medium">{user.email}</div>
            </div>
            <div className="w-10 h-10 rounded-full bg-[var(--unigr-navy)] text-white flex items-center justify-center font-semibold" data-testid="user-avatar">
              {(user.name || "U")[0].toUpperCase()}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => { await logout(); navigate("/login"); }}
              data-testid="logout-btn"
              className="text-slate-600"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </header>

        <main className="flex-1 p-6 md:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
